/**
 * @module engine3d/solid/part
 * @summary The part document: a stack of parametric features, the way a CAD
 *   package's feature tree holds one.
 * @description
 *   A part is an ORDERED LIST of features. Each feature is a solid primitive
 *   plus its parameters, its placement and its material — nothing else. Editing
 *   a number re-evaluates the stack; no geometry is ever baked, so every
 *   dimension stays live for as long as the part exists.
 *
 *   What this deliberately is NOT: a boolean solid modeller. There is no CSG
 *   here — features add material, they never cut it. That is the honest limit of
 *   a triangle-soup renderer with a painter's sort and no depth buffer, and it
 *   costs less than it sounds: the fabricated parts this tool exists to draw —
 *   hoppers, chutes, feeders, silos, stands — are assemblies of plate and rolled
 *   section, which is exactly what a stack of lofts, cones and boxes describes.
 *   Pockets and holes are the documented next step.
 *
 *   UNITS: every parameter and placement figure is in MILLIMETRES, because that
 *   is what a fabrication drawing is dimensioned in. `mmPerUnit` converts to the
 *   world units the shared renderer works in, and defaults to the same 25 the
 *   plant view uses — so a part modelled here is already the right size if it is
 *   later dropped into a plant layout.
 *
 *   // INVARIANT: this module is pure data — no geometry, no DOM, no engine.
 */
import type { MatKey } from '../materials';

/** The solid primitives a part can be built from. */
export type FeatureKind =
  | 'box'
  | 'cyl'
  | 'cone'
  | 'loft'
  | 'prism'
  | 'sphere'
  | 'pipe'
  | 'elbow'
  | 'ring'
  | 'helix'
  | 'vessel'
  | 'sqround'
  | 'shell'
  | 'trough'
  | 'section'
  | 'sketch';

/** One numeric parameter, and everything the UI needs to render a control. */
export interface ParamSpec {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  unit?: 'mm' | 'deg' | 'x' | '';
  /** Present for enumerated parameters — rendered as a dropdown, not a slider. */
  choices?: { v: number; label: string }[];
  hint?: string;
}

/**
 * A repeat of a feature. Two directions, because the four legs under a hopper
 * are a 2 x 2 grid, and describing that as two separate patterns is how a
 * feature tree turns into a mess.
 */
export interface Pattern {
  kind: 'linear' | 'circular';
  count: number;
  /** Linear: pitch between instances. Circular: radius of the bolt circle. */
  spacing: number;
  axis: 'x' | 'y' | 'z';
  /** Circular: total sweep in degrees (360 for a full ring). */
  sweep?: number;
  /** Optional second linear direction. */
  count2?: number;
  spacing2?: number;
  axis2?: 'x' | 'y' | 'z';
}

/**
 * A hand-drawn profile, and what to do with it.
 *
 * This is the escape hatch from the catalogue. Everything else in `FeatureKind`
 * is a shape somebody decided in advance was worth having; a sketch is whatever
 * the job needs, which is the whole point of having one.
 *
 * Points are in millimetres on the sketch plane, RELATIVE to the feature's own
 * placement — so moving the feature moves the profile with it, and re-opening a
 * sketch to edit puts the vertices back under the cursor.
 */
export interface Sketch {
  /**
   * Which plane it was drawn on:
   *   'xz' plan — horizontal axis X, vertical axis Z, the solid grows upward;
   *   'xy' front — X and Y, the solid grows toward +Z;
   *   'zy' side — Z and Y, the solid grows toward +X.
   */
  plane: 'xz' | 'xy' | 'zy';
  /** How the profile becomes a solid. */
  op: 'extrude' | 'revolve' | 'sweep';
  /** The profile, or the path for a sweep. */
  pts: { x: number; y: number }[];
}

export interface Feature {
  id: string;
  kind: FeatureKind;
  /** Shown in the feature tree; the user can rename it. */
  name: string;
  mat: MatKey;
  /** Parameters, keyed by the spec table below. Millimetres or degrees. */
  p: Record<string, number>;
  /** Placement of the primitive's local origin, in millimetres. */
  x: number;
  y: number;
  z: number;
  /** Rotation in degrees, applied X then Y then Z. */
  rx: number;
  ry: number;
  rz: number;
  /** Suppressed features stay in the tree but are not built — as in CAD. */
  suppressed?: boolean;
  pattern?: Pattern;
  /** Present only on a `sketch` feature — the outline the user drew. */
  sketch?: Sketch;
}

export interface Part {
  name: string;
  /** Millimetres per renderer world unit. */
  mmPerUnit: number;
  features: Feature[];
}

// ─── Parameter tables ────────────────────────────────────────────────────

const MM = (key: string, label: string, min: number, max: number, step = 5): ParamSpec => ({
  key,
  label,
  min,
  max,
  step,
  unit: 'mm',
});

const SEG: ParamSpec = {
  key: 'seg',
  label: 'Facets',
  min: 3,
  max: 64,
  step: 1,
  unit: '',
  hint: 'Sides used to approximate the curve — raise it for a smooth barrel, drop it to 8 for a bolted octagon',
};

const END_CHOICES = [
  { v: 0, label: 'Flat' },
  { v: 1, label: 'Dished' },
  { v: 2, label: 'Conical' },
];

/** What each kind is, what it is for, and how it is dimensioned. */
export const KINDS: Record<
  FeatureKind,
  {
    label: string;
    icon: string;
    blurb: string;
    params: ParamSpec[];
    defaults: Record<string, number>;
    mat: MatKey;
  }
> = {
  box: {
    label: 'Box',
    icon: '▭',
    blurb: 'Plate, panel, frame member, bin wall',
    params: [MM('w', 'Width', 5, 12000), MM('h', 'Height', 5, 12000), MM('d', 'Depth', 5, 12000)],
    defaults: { w: 600, h: 400, d: 400 },
    mat: 'galv',
  },
  cyl: {
    label: 'Cylinder',
    icon: '⬭',
    blurb: 'Shell, drum, roller, shaft, stub',
    params: [MM('r', 'Radius', 2, 6000), MM('h', 'Height', 2, 12000), SEG],
    defaults: { r: 300, h: 800, seg: 28 },
    mat: 'steel',
  },
  cone: {
    label: 'Cone / taper',
    icon: '▽',
    blurb: 'Round hopper cone, cyclone body, reducer',
    params: [
      MM('r0', 'Bottom radius', 0, 6000),
      MM('r1', 'Top radius', 0, 6000),
      MM('h', 'Height', 2, 12000),
      SEG,
    ],
    defaults: { r0: 120, r1: 700, h: 900, seg: 28 },
    mat: 'galv',
  },
  loft: {
    label: 'Square hopper',
    icon: '⧨',
    blurb: 'Four-plate hopper, chute transition, tapered bin',
    params: [
      MM('w0', 'Outlet width', 0, 8000),
      MM('d0', 'Outlet depth', 0, 8000),
      MM('w1', 'Top width', 0, 8000),
      MM('d1', 'Top depth', 0, 8000),
      MM('h', 'Height', 2, 12000),
    ],
    defaults: { w0: 300, d0: 300, w1: 1600, d1: 1600, h: 1100 },
    mat: 'galv',
  },
  prism: {
    label: 'Prism',
    icon: '⬡',
    blurb: 'Bolted octagonal shell, hex boss, nut',
    params: [
      MM('r', 'Across corners', 5, 6000),
      MM('h', 'Height', 2, 12000),
      { key: 'n', label: 'Sides', min: 3, max: 24, step: 1, unit: '' },
    ],
    defaults: { r: 400, h: 600, n: 8 },
    mat: 'machine',
  },
  sphere: {
    label: 'Sphere',
    icon: '●',
    blurb: 'Ball valve body, dome, radiused end',
    params: [MM('r', 'Radius', 2, 4000), SEG],
    defaults: { r: 250, seg: 28 },
    mat: 'stainless',
  },
  pipe: {
    label: 'Pipe',
    icon: '▮',
    blurb: 'Spool piece, spout, nozzle — flanges optional',
    params: [
      MM('r', 'Radius', 2, 2000),
      MM('len', 'Length', 10, 20000),
      {
        key: 'flange',
        label: 'Ends',
        min: 0,
        max: 1,
        step: 1,
        choices: [
          { v: 0, label: 'Plain' },
          { v: 1, label: 'Flanged' },
        ],
      },
      SEG,
    ],
    defaults: { r: 100, len: 700, flange: 1, seg: 24 },
    mat: 'pipe',
  },
  elbow: {
    label: 'Elbow',
    icon: '⌐',
    blurb: 'Bend in a spout or duct run',
    params: [
      MM('R', 'Bend radius', 10, 4000),
      MM('r', 'Pipe radius', 2, 1000),
      { key: 'sweep', label: 'Sweep', min: 5, max: 180, step: 5, unit: 'deg' },
      SEG,
    ],
    defaults: { R: 300, r: 100, sweep: 90, seg: 20 },
    mat: 'pipe',
  },
  ring: {
    label: 'Ring',
    icon: '◎',
    blurb: 'Stiffener, rim, flange ring, gasket',
    params: [MM('R', 'Ring radius', 10, 6000), MM('r', 'Section radius', 2, 800), SEG],
    defaults: { R: 700, r: 30, seg: 32 },
    mat: 'steel',
  },
  helix: {
    label: 'Screw flight',
    icon: '≋',
    blurb: 'Auger or screw-feeder flight',
    params: [
      MM('rIn', 'Shaft radius', 2, 1000),
      MM('rOut', 'Flight radius', 5, 1500),
      MM('pitch', 'Pitch', 10, 3000),
      { key: 'turns', label: 'Turns', min: 0.25, max: 20, step: 0.25, unit: 'x' },
      MM('t', 'Plate', 1, 60, 1),
    ],
    defaults: { rIn: 60, rOut: 190, pitch: 340, turns: 6, t: 8 },
    mat: 'stainless',
  },
  sqround: {
    label: 'Square to round',
    icon: '◎',
    blurb: 'Transition between a square outlet and a round spout',
    params: [
      MM('w', 'Square width', 10, 6000),
      MM('d', 'Square depth', 10, 6000),
      MM('r', 'Round radius', 5, 3000),
      MM('h', 'Height', 5, 6000),
      {
        key: 'flip',
        label: 'Round end',
        min: 0,
        max: 1,
        step: 1,
        choices: [
          { v: 0, label: 'At the top' },
          { v: 1, label: 'At the bottom' },
        ],
      },
      SEG,
    ],
    defaults: { w: 600, d: 600, r: 200, h: 500, flip: 0, seg: 32 },
    mat: 'galv',
  },
  shell: {
    label: 'Open shell',
    icon: '◍',
    blurb: 'Rolled shell with real wall thickness — open both ends',
    params: [MM('r', 'Outside radius', 20, 6000), MM('wall', 'Wall', 1, 200, 1), MM('h', 'Height', 5, 12000), SEG],
    defaults: { r: 700, wall: 8, h: 1200, seg: 32 },
    mat: 'stainless',
  },
  trough: {
    label: 'U trough',
    icon: '⋃',
    blurb: 'Screw casing or feeder pan — runs along X, open on top',
    params: [
      MM('r', 'Radius', 20, 2000),
      MM('sideH', 'Side height', 0, 3000),
      MM('wall', 'Wall', 1, 100, 1),
      MM('len', 'Length', 50, 20000),
    ],
    defaults: { r: 250, sideH: 260, wall: 6, len: 2200 },
    mat: 'stainless',
  },
  section: {
    label: 'Steel section',
    icon: 'I',
    blurb: 'Rolled section: beam, channel, angle or hollow',
    params: [
      {
        key: 'shape',
        label: 'Shape',
        min: 0,
        max: 3,
        step: 1,
        choices: [
          { v: 0, label: 'I-beam' },
          { v: 1, label: 'Channel' },
          { v: 2, label: 'Angle' },
          { v: 3, label: 'Hollow / flat' },
        ],
      },
      MM('dep', 'Depth', 20, 1200),
      MM('wid', 'Width', 20, 1200),
      MM('t', 'Thickness', 1, 60, 1),
      MM('len', 'Length', 50, 20000),
    ],
    defaults: { shape: 0, dep: 200, wid: 100, t: 8, len: 2400 },
    mat: 'column',
  },
  sketch: {
    label: 'Sketch',
    icon: '✎',
    blurb: 'A profile you drew yourself — extruded, revolved or swept',
    params: [
      MM('h', 'Depth / height', 1, 12000),
      MM('r', 'Sweep radius', 2, 2000),
      MM('bend', 'Sweep bend radius', 0, 3000),
      SEG,
    ],
    defaults: { h: 400, r: 100, bend: 150, seg: 32 },
    mat: 'galv',
  },
  vessel: {
    label: 'Vessel',
    icon: '⬯',
    blurb: 'Tank or silo shell with dished, flat or conical ends',
    params: [
      MM('r', 'Radius', 20, 6000),
      MM('h', 'Shell height', 10, 20000),
      { key: 'endBot', label: 'Bottom end', min: 0, max: 2, step: 1, choices: END_CHOICES },
      { key: 'endTop', label: 'Top end', min: 0, max: 2, step: 1, choices: END_CHOICES },
      MM('coneH', 'Cone height', 0, 6000),
      SEG,
    ],
    defaults: { r: 900, h: 2200, endBot: 2, endTop: 1, coneH: 1100, seg: 32 },
    mat: 'steel',
  },
};

/** The order the palette lists them in — commonest first, not alphabetical. */
export const KIND_ORDER: FeatureKind[] = [
  'loft',
  'cone',
  'cyl',
  'box',
  'sqround',
  'vessel',
  'pipe',
  'elbow',
  'section',
  'trough',
  'shell',
  'helix',
  'prism',
  'ring',
  'sphere',
];

/**
 * Which parameters a sketch actually uses, given its operation. The panel would
 * otherwise offer a sweep radius on an extrusion, which is a dimension with no
 * effect — the worst kind of control to put in front of an engineer.
 */
export function sketchParams(op: Sketch['op']): string[] {
  if (op === 'extrude') return ['h'];
  if (op === 'revolve') return ['seg'];
  return ['r', 'bend', 'seg'];
}

/** Materials offered in the part modeller, in a sensible fabrication order. */
export const PART_MATERIALS: { key: MatKey; label: string }[] = [
  { key: 'galv', label: 'Galvanised' },
  { key: 'steel', label: 'Mild steel' },
  { key: 'stainless', label: 'Stainless' },
  { key: 'machine', label: 'Machined' },
  { key: 'painted', label: 'Painted' },
  { key: 'cast', label: 'Cast iron' },
  { key: 'motor', label: 'Motor / drive' },
  { key: 'pipe', label: 'Pipework' },
  { key: 'lagging', label: 'Lagging' },
  { key: 'rubber', label: 'Rubber' },
  { key: 'plastic', label: 'Plastic' },
  { key: 'safety', label: 'Safety yellow' },
  { key: 'accent', label: 'Accent orange' },
  { key: 'column', label: 'Structural' },
  { key: 'concrete', label: 'Concrete' },
  { key: 'glass', label: 'Sight glass' },
];

// ─── Construction ────────────────────────────────────────────────────────

let seq = 0;

/** Keep new ids clear of the ones a loaded part brought with it. */
export function syncSeq(part: Part): void {
  for (const f of part.features) {
    const n = Number(/(\d+)$/.exec(f.id)?.[1] ?? 0);
    if (n > seq) seq = n;
  }
}

/** The next free feature id. Exported so a copy can be made without a rebuild. */
export function nextId(): string {
  return `f${++seq}`;
}

export function mkFeature(kind: FeatureKind, over: Partial<Feature> = {}): Feature {
  const k = KINDS[kind];
  return {
    id: `f${++seq}`,
    kind,
    name: k.label,
    mat: k.mat,
    x: 0,
    y: 0,
    z: 0,
    rx: 0,
    ry: 0,
    rz: 0,
    ...over,
    // Merged rather than replaced, so a caller can override one dimension and
    // still get sensible values for the rest.
    p: { ...k.defaults, ...(over.p ?? {}) },
  };
}

export function emptyPart(name = 'Untitled part'): Part {
  return { name, mmPerUnit: 25, features: [] };
}

export const clonePart = (p: Part): Part => JSON.parse(JSON.stringify(p)) as Part;

/**
 * Give a feature a unique name within the part, the way a feature tree does:
 * `Square hopper`, `Square hopper2`, … Renaming by hand is still allowed.
 */
export function uniqueName(part: Part, base: string): string {
  const taken = new Set(part.features.map((f) => f.name));
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) if (!taken.has(base + i)) return base + i;
}

/** How many instances a feature's pattern produces, itself included. */
export function instanceCount(f: Feature): number {
  const q = f.pattern;
  if (!q) return 1;
  const a = Math.max(1, Math.round(q.count));
  const b = q.kind === 'linear' ? Math.max(1, Math.round(q.count2 ?? 1)) : 1;
  return a * b;
}
